import React, { forwardRef, InputHTMLAttributes } from 'react';
import { cn } from '@/utils/cn';

export interface InputMaskProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  mask?: 'cpf' | 'cnpj' | 'phone' | 'cep' | 'date' | 'currency' | 'cpf-cnpj';
  onChange?: (value: string, rawValue: string) => void;
  error?: boolean;
}

const masks = {
  cpf: '999.999.999-99',
  cnpj: '99.999.999/9999-99',
  phone: '(99) 9 9999-9999',
  cep: '99999-999',
  date: '99/99/9999',
  currency: 'R$ 9.999.999,99',
};

const applyMask = (value: string, mask: string): string => {
  let maskedValue = '';
  let valueIndex = 0;

  for (let i = 0; i < mask.length && valueIndex < value.length; i++) {
    if (mask[i] === '9') {
      maskedValue += value[valueIndex];
      valueIndex++;
    } else {
      maskedValue += mask[i];
      if (value[valueIndex] === mask[i]) {
        valueIndex++;
      }
    }
  }

  return maskedValue;
};

const removeMask = (value: string): string => {
  return value.replace(/\D/g, '');
};

const applyCPFMask = (value: string): string => {
  const numbers = removeMask(value);
  return numbers
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})/, '$1-$2')
    .replace(/(-\d{2})\d+?$/, '$1');
};

const applyCNPJMask = (value: string): string => {
  const numbers = removeMask(value);
  return numbers
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2')
    .replace(/(-\d{2})\d+?$/, '$1');
};

const applyCPFOrCNPJMask = (value: string): string => {
  const numbers = removeMask(value);
  if (numbers.length <= 11) {
    return applyCPFMask(value);
  }
  return applyCNPJMask(value);
};

const applyPhoneMask = (value: string): string => {
  const numbers = removeMask(value).slice(0, 11);
  if (numbers.length === 0) return '';
  if (numbers.length <= 2) return `(${numbers}`;
  if (numbers.length <= 6) {
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
  }
  if (numbers.length <= 10) {
    // Fixo: (00) 0000-0000
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
  }
  // Celular: (00) 0 0000-0000
  return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 3)} ${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
};

const applyCEPMask = (value: string): string => {
  const numbers = removeMask(value);
  return numbers
    .replace(/(\d{5})(\d)/, '$1-$2')
    .replace(/(-\d{3})\d+?$/, '$1');
};

const applyDateMask = (value: string): string => {
  const numbers = removeMask(value);
  return numbers
    .replace(/(\d{2})(\d)/, '$1/$2')
    .replace(/(\d{2})(\d)/, '$1/$2')
    .replace(/(\/\d{4})\d+?$/, '$1');
};

const applyCurrencyMask = (value: string): string => {
  const numbers = removeMask(value);
  const numberValue = Number(numbers) / 100;
  
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(numberValue);
};

export const InputMask = forwardRef<HTMLInputElement, InputMaskProps>(
  ({ mask, onChange, className, error, ...props }, ref) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let { value } = e.target;
      let maskedValue = value;
      let rawValue = value;

      if (mask) {
        rawValue = removeMask(value);

        switch (mask) {
          case 'cpf':
            maskedValue = applyCPFMask(value);
            break;
          case 'cnpj':
            maskedValue = applyCNPJMask(value);
            break;
          case 'cpf-cnpj':
            maskedValue = applyCPFOrCNPJMask(value);
            break;
          case 'phone':
            maskedValue = applyPhoneMask(value);
            break;
          case 'cep':
            maskedValue = applyCEPMask(value);
            break;
          case 'date':
            maskedValue = applyDateMask(value);
            break;
          case 'currency':
            maskedValue = applyCurrencyMask(value);
            break;
          default:
            if (masks[mask as keyof typeof masks]) {
              maskedValue = applyMask(rawValue, masks[mask as keyof typeof masks]);
            }
        }
      }

      if (onChange) {
        onChange(maskedValue, rawValue);
      }

      // Atualizar o valor do input
      e.target.value = maskedValue;
    };

    return (
      <input
        ref={ref}
        type="text"
        onChange={handleChange}
        className={cn(
          'flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm',
          'placeholder:text-gray-400',
          'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
          'disabled:cursor-not-allowed disabled:opacity-50',
          error && 'border-red-500 focus:ring-red-500',
          className
        )}
        {...props}
      />
    );
  }
);

InputMask.displayName = 'InputMask';
